import { SyntheticEvent, useCallback } from 'react';
import { AutocompleteChangeReason } from '@mui/material/Autocomplete';
import Autocomplete, {
  AutocompleteProps,
  Option,
} from 'fe-design-base/atoms/Autocomplete';
import FormField, { FormFieldProps } from 'fe-design-base/atoms/FormField';
import { Field, FormikValues, useFormikContext } from 'formik';

export type AutocompleteFieldProps = {
  selectedOption?: Option | null;
  dataTestId?: string;
} & AutocompleteProps &
  Omit<FormFieldProps, 'children'>;

const AutocompleteField = ({
  name,
  label,
  inputId,
  width,
  fullWidth,
  labelPosition,
  labelWidth,
  shrink,
  disabled,
  onChange,
  uxElement,
  options,
  hasAsterisk,
  helperText,
  successText,
  dataTestId,
  ...inputProps
}: AutocompleteFieldProps) => {
  const { values, touched, errors, setFieldValue } =
    useFormikContext<FormikValues>();
  const error = touched?.[name] && errors?.[name] && !disabled;

  const handleChange = useCallback(
    (
      e: SyntheticEvent,
      newValue: Option | null,
      reason: AutocompleteChangeReason
    ) => {
      const selectedOptionLabel =
        typeof options[0] === 'string'
          ? newValue?.label
          : (options as Option[]).find(
              option => option?.label === newValue?.label
            )?.label;

      setFieldValue(name, selectedOptionLabel);

      onChange?.(e, newValue, reason);
    },
    [name, onChange, options, setFieldValue]
  );

  const isRequired = hasAsterisk && !disabled && !inputProps.readOnly;

  return (
    <FormField
      label={label}
      name={name}
      inputId={inputId}
      width={width}
      fullWidth={fullWidth}
      labelPosition={labelPosition}
      labelWidth={labelWidth}
      shrink={shrink}
      disabled={disabled}
      hasAsterisk={hasAsterisk}
      helperText={helperText}
      successText={successText}
    >
      <Field
        as={Autocomplete}
        name={name}
        id={inputId}
        disabled={disabled}
        fullWidth={fullWidth}
        label={label}
        error={error}
        uxElement={uxElement}
        onChange={handleChange}
        options={options}
        value={values[name]}
        dataTestId={dataTestId}
        required={isRequired}
        {...inputProps}
      />
    </FormField>
  );
};

export default AutocompleteField;
